import './vehicleConfirmationPage.scss';

import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import KeyboardBackspaceRoundedIcon from '@mui/icons-material/KeyboardBackspaceRounded';
import MenuRoundedIcon from '@mui/icons-material/MenuRounded';
import LoadingButton from '@mui/lab/LoadingButton';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Box from '@mui/material/Box';
import red from '@mui/material/colors/red';
import MenuItem from '@mui/material/MenuItem';
import Snackbar from '@mui/material/Snackbar';
import ThemeProvider from '@mui/material/styles/ThemeProvider';
import TextField from '@mui/material/TextField';
import { isAutopayNumber } from '@thesavingsgroup/utils/isAutopayNumber';
import { isEmpty, rangeRight, toString } from 'lodash';
import { ChangeEvent, useEffect } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useLocation, useParams } from 'react-router-dom';

import { CircularProgressFullScreen } from '../../components/shared_new/CircularProgressFullScreen/CircularProgressFullScreen';
import { NavigationPane } from '../../components/shared_new/NavigationPane/NavigationPane';
import { resetStore } from '../../store/_core/resetStore';
import useApiStore from '../../store/apiStore/useApiStore';
import useDashboardStore from '../../store/dashboardStore/useDashboardStore';
import useNavigationPaneStore from '../../store/navigationPaneStore/useNavigationPaneStore';
import useParameterStore from '../../store/parameterStore/useParameterStore';
import { useNavigateWithCallback } from '../../store/routeStore/hooks/useNavigateWithCallback';
import useThemeStore from '../../store/themeStore/useThemeStore';
import { UpdateVehicleParams } from '../../store/vehicleConfirmationStore/types/UpdateVehicleParams';
import useVehicleConfirmationStore from '../../store/vehicleConfirmationStore/useVehicleConfirmationStore';
import { locator } from '../../testing/e2e-locator/locator';

export function Component(): JSX.Element {
  const navigateWithCallback = useNavigateWithCallback();
  const location = useLocation();
  const { vehicleId } = useParams();

  const { createTheme } = useThemeStore();
  const theme = createTheme();

  const { isLoading } = useApiStore();
  const { t } = useTranslation();
  const { setDrawerOpen } = useNavigationPaneStore();

  const { hasDuplicateApplication, clearHasDuplicateApplication } =
    useParameterStore();

  const {
    computed: {
      isVehicleConfirmed,
      isVehicleEmpty,
      vehicleApplicationInSubmission,
      vehicleYearMakeModel,
    },
    fetchVehicleByApplicationAutopayNumberFailedMessage,
    fetchVehicleByIdFailedMessage,
    isLoadingTransition,
    fetchVehicleById,
    fetchVehicleByApplicationAutopayNumber,
  } = useDashboardStore();

  const {
    fetchVehicleMakesFailedMessage,
    fetchVehicleModelsFailedMessage,
    fetchVehicleTrimsFailedMessage,
    makes,
    models,
    trims,
    updateVehicleFailedMessage,
    updateVehicleSuccess,
    fetchVehicleMakes,
    fetchVehicleModels,
    fetchVehicleTrims,
    updateVehicle,
  } = useVehicleConfirmationStore();

  const {
    getValues,
    setValue,
    register,
    handleSubmit,
    watch,
    formState: { errors, isValid },
  } = useForm({
    mode: 'onBlur',
    delayError: 200,
  });

  const yearValidation = {
    onChange: async (event: ChangeEvent<HTMLInputElement>): Promise<void> => {
      setValue('makeValue', '');
      setValue('modelValue', '');
      setValue('trimValue', '');

      await fetchVehicleMakes(event.target.value);
    },
    required: t('validation.year.required'),
  };

  const makeValidation = {
    onChange: async (event: ChangeEvent<HTMLInputElement>): Promise<void> => {
      setValue('modelValue', '');
      setValue('trimValue', '');

      await fetchVehicleModels(getValues('yearValue'), event.target.value);
    },
    required: t('validation.make.required'),
  };

  const modelValidation = {
    onChange: async (event: ChangeEvent<HTMLInputElement>): Promise<void> => {
      setValue('trimValue', '');

      await fetchVehicleTrims(
        getValues('yearValue'),
        getValues('makeValue'),
        event.target.value,
      );
    },
    required: t('validation.model.required'),
  };

  const trimValidation = {
    required: t('validation.trim.required'),
  };

  const errorMessage =
    fetchVehicleByApplicationAutopayNumberFailedMessage ||
    fetchVehicleByIdFailedMessage ||
    fetchVehicleMakesFailedMessage ||
    fetchVehicleModelsFailedMessage ||
    fetchVehicleTrimsFailedMessage ||
    updateVehicleFailedMessage ||
    null;

  const YEARS_OF_VEHICLES_TO_SHOW = 20;
  const INCLUDE_NEXT_YEAR_OFFSET = 2;

  // Present 20 years of vehicles starting from this year whilst including next year.
  // lodash range does not include its end parameter, so we add 2 to include next year
  const vehicleYears = rangeRight(
    new Date().getFullYear() - YEARS_OF_VEHICLES_TO_SHOW,
    new Date().getFullYear() + INCLUDE_NEXT_YEAR_OFFSET,
  ).map(toString);

  const showDuplicateApplicationNotification =
    hasDuplicateApplication === 'true' && !isVehicleEmpty;

  const shouldNavigateToDashboardGarage =
    (!isLoadingTransition && isVehicleConfirmed && !isVehicleEmpty) ||
    vehicleApplicationInSubmission;

  const navigateToDashboardGarage = (): void => {
    navigateWithCallback('/dashboard/garage').then(() => {
      resetStore(useVehicleConfirmationStore);
    });
  };

  const navigateToDashboardApplication = (): void => {
    navigateWithCallback(`/dashboard/application/${vehicleId}`).then(() => {
      resetStore(useVehicleConfirmationStore);
    });
  };

  const formSubmit = async (values: FieldValues): Promise<void> => {
    const { yearValue, makeValue, modelValue, trimValue } = values;

    if (!yearValue || !makeValue || !modelValue || !trimValue) {
      return;
    }

    const make = makes.find((aMake) => aMake.value === makeValue).label;
    const model = models.find((aModel) => aModel.value === modelValue).label;
    const trim = trims.find((aTrim) => aTrim.value === trimValue);

    const params: UpdateVehicleParams = {
      vehicle: {
        make,
        model,
        trim: trim.label,
        trimId: trim.value,
        year: yearValue,
      },
      vehicleId,
    };

    await updateVehicle(params);
  };

  useEffect(() => {
    if (isAutopayNumber(vehicleId)) {
      fetchVehicleByApplicationAutopayNumber(vehicleId);

      return;
    }

    fetchVehicleById(vehicleId);
  }, []);

  useEffect(() => {
    return (): void => {
      // Clear duplicate application notification when navigating away
      clearHasDuplicateApplication();
    };
  }, [location.pathname]);

  useEffect(() => {
    // navigate to dashboard application if vehicle is updated successfully
    if (updateVehicleSuccess) {
      navigateToDashboardApplication();
    }
  }, [updateVehicleSuccess]);

  useEffect(() => {
    // navigate to dashboard garage if vehicle is confirmed or the vehicle application is in submission
    if (shouldNavigateToDashboardGarage) {
      navigateToDashboardGarage();
    }
  }, [shouldNavigateToDashboardGarage]);

  if (isLoadingTransition || shouldNavigateToDashboardGarage) {
    return <CircularProgressFullScreen />;
  }

  return (
    <div className="vehicle-confirmation-page">
      <ThemeProvider theme={theme}>
        <Box sx={theme.pageLayout}>
          {showDuplicateApplicationNotification && (
            <Snackbar
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              open
            >
              <Alert
                data-testid={
                  locator.pages.vehicleConfirmation.alert.duplicateApplication
                }
                icon={false}
                severity="info"
                variant="filled"
                onClose={clearHasDuplicateApplication}
              >
                <AlertTitle>{vehicleYearMakeModel}</AlertTitle>
                {t('notifications.duplicateApplication')}
              </Alert>
            </Snackbar>
          )}

          <header data-testid={locator.pages.vehicleConfirmation.header.root}>
            <button
              aria-label="Back to dashboard garage"
              data-testid={locator.pages.vehicleConfirmation.header.backButton}
              type="button"
              onClick={navigateToDashboardGarage}
            >
              <KeyboardBackspaceRoundedIcon />
            </button>
            <button
              aria-label="Toggle drawer"
              data-testid={
                locator.pages.vehicleConfirmation.header.drawerButton
              }
              type="button"
              onClick={() => {
                setDrawerOpen(true);
              }}
            >
              <MenuRoundedIcon />
            </button>
          </header>

          <main>
            <form
              data-testid={locator.pages.vehicleConfirmation.form.root}
              noValidate
              onSubmit={handleSubmit(formSubmit)}
            >
              <div className="form-content">
                <h1 data-testid={locator.pages.vehicleConfirmation.form.h1}>
                  {t('VehicleConfirmationPage.title')}
                </h1>
                <h2 data-testid={locator.pages.vehicleConfirmation.form.h2}>
                  {t('VehicleConfirmationPage.subtitle')}
                </h2>

                {!isEmpty(errorMessage) && (
                  <div style={{ marginBottom: '2rem' }}>
                    <Alert
                      data-testid={
                        locator.pages.vehicleConfirmation.alert
                          .fetchOrUpdateError
                      }
                      icon={false}
                      severity="error"
                    >
                      {errorMessage}
                    </Alert>
                  </div>
                )}

                <div style={{ marginBottom: '3rem' }}>
                  <div>
                    <TextField
                      {...register('yearValue', yearValidation)}
                      SelectProps={{
                        IconComponent: ExpandMoreRoundedIcon,
                      }}
                      error={!isEmpty(errors.yearValue)}
                      inputProps={{
                        'data-testid':
                          locator.pages.vehicleConfirmation.form.yearSelect,
                      }}
                      label={t('VehicleConfirmationPage.selectYear')}
                      value={watch('yearValue', '')}
                      fullWidth
                      select
                    >
                      <MenuItem value="" disabled>
                        {t('VehicleConfirmationPage.selectYear')}
                      </MenuItem>

                      {vehicleYears.map((year) => {
                        return (
                          <MenuItem key={year} value={year}>
                            {year}
                          </MenuItem>
                        );
                      })}
                    </TextField>

                    {!isEmpty(errors.yearValue) && (
                      <span
                        data-testid={
                          locator.pages.vehicleConfirmation.form.yearSelectError
                        }
                        style={{ fontSize: '12px', color: red[700] }}
                      >
                        {toString(errors.yearValue.message)}
                      </span>
                    )}
                  </div>

                  <div style={{ marginTop: '1.5rem' }}>
                    <TextField
                      {...register('makeValue', makeValidation)}
                      SelectProps={{
                        IconComponent: ExpandMoreRoundedIcon,
                      }}
                      disabled={!watch('yearValue')}
                      error={!isEmpty(errors.makeValue)}
                      inputProps={{
                        'data-testid':
                          locator.pages.vehicleConfirmation.form.makeSelect,
                      }}
                      label={t('VehicleConfirmationPage.selectMake')}
                      value={watch('makeValue') || ''}
                      fullWidth
                      select
                    >
                      <MenuItem value="" disabled>
                        {t('VehicleConfirmationPage.selectMake')}
                      </MenuItem>

                      {makes.map(({ label, value }) => {
                        return (
                          <MenuItem key={value} value={value}>
                            {label}
                          </MenuItem>
                        );
                      })}
                    </TextField>

                    {!isEmpty(errors.makeValue) && (
                      <span
                        data-testid={
                          locator.pages.vehicleConfirmation.form.makeSelectError
                        }
                        style={{ fontSize: '12px', color: red[700] }}
                      >
                        {toString(errors.makeValue.message)}
                      </span>
                    )}
                  </div>

                  <div style={{ marginTop: '1.5rem' }}>
                    <TextField
                      {...register('modelValue', modelValidation)}
                      SelectProps={{
                        IconComponent: ExpandMoreRoundedIcon,
                      }}
                      disabled={!watch('yearValue') || !watch('makeValue')}
                      error={!isEmpty(errors.modelValue)}
                      inputProps={{
                        'data-testid':
                          locator.pages.vehicleConfirmation.form.modelSelect,
                      }}
                      label={t('VehicleConfirmationPage.selectModel')}
                      value={watch('modelValue') || ''}
                      fullWidth
                      select
                    >
                      <MenuItem value="" disabled>
                        {t('VehicleConfirmationPage.selectModel')}
                      </MenuItem>

                      {models.map(({ label, value }) => {
                        return (
                          <MenuItem key={value} value={value}>
                            {label}
                          </MenuItem>
                        );
                      })}
                    </TextField>

                    {!isEmpty(errors.modelValue) && (
                      <span
                        data-testid={
                          locator.pages.vehicleConfirmation.form
                            .modelSelectError
                        }
                        style={{ fontSize: '12px', color: red[700] }}
                      >
                        {toString(errors.modelValue.message)}
                      </span>
                    )}
                  </div>

                  <div style={{ marginTop: '1.5rem' }}>
                    <TextField
                      {...register('trimValue', trimValidation)}
                      SelectProps={{
                        IconComponent: ExpandMoreRoundedIcon,
                      }}
                      disabled={
                        !watch('yearValue') ||
                        !watch('makeValue') ||
                        !watch('modelValue')
                      }
                      error={!isEmpty(errors.trimValue)}
                      inputProps={{
                        'data-testid':
                          locator.pages.vehicleConfirmation.form.trimSelect,
                      }}
                      label={t('VehicleConfirmationPage.selectTrim')}
                      value={watch('trimValue') || ''}
                      fullWidth
                      select
                    >
                      <MenuItem value="" disabled>
                        {t('VehicleConfirmationPage.selectTrim')}
                      </MenuItem>

                      {trims.map(({ label, value }) => {
                        return (
                          <MenuItem key={value} value={value}>
                            {label}
                          </MenuItem>
                        );
                      })}
                    </TextField>

                    {!isEmpty(errors.trimValue) && (
                      <span
                        data-testid={
                          locator.pages.vehicleConfirmation.form.trimSelectError
                        }
                        style={{ fontSize: '12px', color: red[700] }}
                      >
                        {toString(errors.trimValue.message)}
                      </span>
                    )}
                  </div>
                </div>

                <div style={{ marginBottom: '3rem' }}>
                  <LoadingButton
                    color="primary"
                    data-testid={
                      locator.pages.vehicleConfirmation.form.submitButton
                    }
                    disabled={!isValid}
                    endIcon={<></>}
                    loading={isLoading}
                    loadingPosition="end"
                    type="submit"
                    variant="contained"
                    disableElevation
                    fullWidth
                  >
                    {t('VehicleConfirmationPage.confirm')}
                  </LoadingButton>
                </div>
              </div>
              <footer
                data-testid={locator.pages.vehicleConfirmation.footer.root}
              >
                {t('copyright', { year: new Date().getFullYear() })}
              </footer>
            </form>
          </main>
        </Box>

        <NavigationPane withDrawer withUserNameHeader />
      </ThemeProvider>
    </div>
  );
}

Component.displayName = 'VehicleConfirmationPage';
